import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { SeriesPoint } from '../../hooks/useAnalyticsQuery.ts';

interface AnalyticsChartProps {
  series: SeriesPoint[];
  comparison?: SeriesPoint[];
  unit: '%' | 'count' | 'points';
  loading?: boolean;
  scannedMb?: number;
  executionMs?: number;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

export function AnalyticsChart({ series, comparison, unit, loading, scannedMb, executionMs }: AnalyticsChartProps) {
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm h-64 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Caricamento...</div>
      </div>
    );
  }

  if (series.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm h-64 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Nessun dato nel periodo selezionato</div>
      </div>
    );
  }

  // Merge main series and comparison into a single data array for Recharts (date-based, not index-based)
  function mergeForChart(
    s: SeriesPoint[],
    comp?: SeriesPoint[],
  ): Record<string, string | number>[] {
    const map = new Map<string, Record<string, string | number>>();

    s.forEach((p) => {
      const key = formatDate(p.date);
      map.set(key, { date: key, valore: p.value });
    });

    comp?.forEach((p) => {
      const key = formatDate(p.date);
      const existing = map.get(key);
      if (existing) {
        existing['confronto'] = p.value;
      } else {
        map.set(key, { date: key, valore: 0, confronto: p.value });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
  }

  const data = mergeForChart(series, comparison);

  const valueFormatter = (v: number) =>
    unit === '%' ? `${v.toFixed(1)}%` : v.toLocaleString('it-IT');

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={valueFormatter}
          />
          <Tooltip formatter={(v) => valueFormatter(v as number)} />
          {comparison && <Legend />}
          <Line
            type="monotone"
            dataKey="valore"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
          {comparison && (
            <Line
              type="monotone"
              dataKey="confronto"
              stroke="#94a3b8"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{ r: 4 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      {(executionMs !== undefined || scannedMb !== undefined) && (
        <div className="mt-2 text-xs text-gray-400 text-right">
          {executionMs !== undefined && scannedMb !== undefined
            ? `Query in ${(executionMs / 1000).toFixed(2)}s · ${scannedMb.toFixed(2)} MB scansionati`
            : executionMs !== undefined
              ? `Query in ${(executionMs / 1000).toFixed(2)}s`
              : `${scannedMb!.toFixed(2)} MB scansionati`}
        </div>
      )}
    </div>
  );
}
